import { Button, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { getDenseResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import {
  MapSearchURLParams,
  SearchResult,
  getGoogleMapsSearchSource,
  getLatLngSearchSource,
  getMapboxMapsSearchSource,
  getInactiveGeoEventSearchSource,
  useMapSearch,
  getGeoEventSearchSource,
  getUrgencyIcon,
  getGeoEventDetails,
  getGeoEventAvatar,
  geoEventHasInternalNotes,
  geoEventHasReporterNotes,
} from 'hooks/useMapSearch';
import { useTranslation } from 'react-i18next';
import { useMap } from 'shared/map-exports';
import { WildfireListItem } from 'features/wildfires/wildfire.types';
import { useMapState } from 'state/useMapState';
import { useAuthState } from 'state/useAuthState';
import { useMapSearchState } from 'state/useMapSearchState';
import { useSelectedLocationState } from 'state/useSelectedLocationState';
import { useQueryParams } from 'hooks/useQueryParams';
import { useMemo } from 'react';
import { FloodingListItem } from 'features/flooding/flooding.types';
import { getViewGeoEventLink } from 'features/geoEvents/geoEvent.utils';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useHistory } from 'react-router-dom';
import { useVisibleGeoEvents } from './useVisibleGeoEvents';
import { useFilteredGeoEvents } from './useFilteredGeoEvents';
import { useShowAdvancedSearch } from './useShowAdvancedSearch';
import { VirtualizedSearchResultsList } from './VirtualizedSearchResultsList';

type SearchResultsProps = {
  onBeforeClickResult?: () => void;
  isSheetOpen?: boolean;
  /**
   * Called on pointer down of controls (e.g. the sort toggle) before they take
   * focus from the search input and hide the on-screen keyboard. Lets the
   * parent keep the mobile bottom sheet from collapsing when the keyboard hides.
   */
  onBeforeFocusDrawerElement?: () => void;
};

export const visibleGeoEventToSearchResult = (
  geoEvent: WildfireListItem | FloodingListItem,
  getHasInternalNotes = geoEventHasInternalNotes,
): SearchResult => ({
  sourceId: 'wildfire',
  action: 'link',
  id: geoEvent.id,
  primaryText: geoEvent.name,
  secondaryText: geoEvent.address ?? undefined,
  geoEventDetails: getGeoEventDetails(geoEvent),
  avatar: getGeoEventAvatar(geoEvent, false),
  href: getViewGeoEventLink(geoEvent.geoEventType ?? 'wildfire', geoEvent.id),
  // Used for end-to-end testing
  // Use case: make sure a reporter can select a reporter-managed geo event and make updates to it
  testId: geoEvent.reporterManaged
    ? `reporter-managed-${geoEvent.id}`
    : undefined,
  urgencyIcon: getUrgencyIcon(geoEvent.data),
  hasInternalNotes: getHasInternalNotes(geoEvent),
});

export const SearchResults = (
  props: SearchResultsProps,
): JSX.Element | JSX.Element[] | null => {
  const {
    onBeforeClickResult,
    isSheetOpen = false,
    onBeforeFocusDrawerElement,
  } = props;
  const { incidentMapState } = useMapState();
  const { lat, lng } = incidentMapState.center;
  const { t } = useTranslation();
  const [{ searchType }] = useQueryParams<MapSearchURLParams>();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');
  const isInternalTabEnabled = useReleaseFlag('internal_tab_on_incidents');
  const getHasInternalNotes = isInternalTabEnabled
    ? geoEventHasReporterNotes
    : geoEventHasInternalNotes;

  // for focusing a pin for adding a place. In the future we should use an href and history.replaceState() for this
  const { setSelectedValue } = useMapSearchState();
  const { setSelectedLocation } = useSelectedLocationState();
  const { default: map } = useMap();
  const history = useHistory();

  const {
    permissions: { isInternalUser },
    showMembershipProFeatures,
  } = useAuthState();

  const showAdvancedSearch = useShowAdvancedSearch(isSheetOpen);

  const isHistoricalSearch =
    searchType === 'inactive' && showMembershipProFeatures;

  const mappingSource = isInternalUser
    ? getGoogleMapsSearchSource({
        map: map?.getMap(),
        history,
        ...(pinImprovements ? {} : { setSelectedValue, setSelectedLocation }),
      })
    : getMapboxMapsSearchSource({
        map: map?.getMap(),
        history,
        ...(pinImprovements ? {} : { setSelectedValue, setSelectedLocation }),
      });

  const sources = isHistoricalSearch
    ? [{ ...getInactiveGeoEventSearchSource(), getHasInternalNotes }]
    : [
        { ...getGeoEventSearchSource(), getHasInternalNotes },
        getLatLngSearchSource({
          map: map?.getMap(),
          history,
          ...(pinImprovements ? {} : { setSelectedValue, setSelectedLocation }),
        }),
        mappingSource,
      ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { searchQuery, searchResults, isLoading } = useMapSearch<any>({
    proximity: `${lng},${lat}`,
    sources,
  });

  const showDefaultGeoEvents = !searchQuery && !isHistoricalSearch;

  const visibleGeoEvents = useVisibleGeoEvents({
    enabled: showDefaultGeoEvents,
  });

  const filteredGeoEvents = useFilteredGeoEvents({
    geoEvents: visibleGeoEvents,
  });

  const { sortDescending, toggleSortDescending } = useSearchDrawerState();

  const sortedGeoEvents = useMemo(() => {
    if (!showDefaultGeoEvents) {
      return filteredGeoEvents;
    }

    return [...filteredGeoEvents].sort((a, b) => {
      const timeA = new Date(a.dateModified).getTime();
      const timeB = new Date(b.dateModified).getTime();
      const timeDiff = sortDescending ? timeB - timeA : timeA - timeB;
      if (timeDiff === 0) {
        return sortDescending ? b.id - a.id : a.id - b.id;
      }
      return timeDiff;
    });
  }, [filteredGeoEvents, sortDescending, showDefaultGeoEvents]);

  const results = useMemo(
    () =>
      showDefaultGeoEvents
        ? sortedGeoEvents.map((geoEvent) =>
            visibleGeoEventToSearchResult(geoEvent, getHasInternalNotes),
          )
        : searchResults,
    [showDefaultGeoEvents, sortedGeoEvents, searchResults, getHasInternalNotes],
  );

  if (isLoading) {
    return Array.from({ length: 10 }).map((_, index) => (
      <Skeleton
        // Key not important since this is the same content repeated
        // eslint-disable-next-line @eslint-react/no-array-index-key
        key={index}
        variant="text"
        height={40}
        sx={{ marginLeft: 2, marginRight: 2 }}
      />
    ));
  }

  if (!results.length && searchQuery) {
    return (
      <>
        <Typography sx={{ margin: 2, textAlign: 'center' }}>
          {t('searchBar.noResults')}
        </Typography>
        {isHistoricalSearch && (
          <Typography sx={{ margin: 2, textAlign: 'center' }}>
            {t('searchBar.noResultsHistoricalNote')}
          </Typography>
        )}
      </>
    );
  }

  if (!results.length) {
    return null;
  }

  const header = (
    <>
      <Stack
        direction="row"
        sx={{
          gap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          pb: 1.5,
          // Extra top gap only when the advanced search controls are showing.
          // Kept here (not in the sticky SearchBar) so it scrolls away instead
          // of leaving dead space under the pinned search bar.
          pt: showAdvancedSearch ? 1.5 : 0,
        }}
      >
        <Typography variant="h3">
          {showDefaultGeoEvents
            ? t('searchBar.incidents')
            : t('searchBar.results')}
        </Typography>
        {showDefaultGeoEvents ? (
          <Button
            disableRipple
            variant="text"
            color="inherit"
            size="small"
            endIcon={
              sortDescending ? (
                <KeyboardArrowDown data-testid="sort-descending-icon" />
              ) : (
                <KeyboardArrowUp data-testid="sort-ascending-icon" />
              )
            }
            onClick={toggleSortDescending}
            onPointerDown={onBeforeFocusDrawerElement}
            sx={{
              minWidth: 'auto',
              paddingX: 0.5,
              '& .MuiButton-endIcon': {
                marginLeft: '4px',
                '& >*:nth-of-type(1)': {
                  fontSize: getDenseResponsiveFontSize('1.5rem'),
                },
              },
            }}
          >
            <Typography variant="body2" component="span" color="gray">
              {t('searchDrawer.lastUpdated')}
            </Typography>
          </Button>
        ) : (
          <Typography
            variant="body2"
            component="h4"
            color="gray"
            alignSelf="end"
          >
            {t('searchDrawer.lastUpdate')}
          </Typography>
        )}
      </Stack>
      <Divider />
    </>
  );

  return (
    <VirtualizedSearchResultsList
      results={results}
      searchTerm={searchQuery}
      onBeforeClick={onBeforeClickResult}
      header={header}
    />
  );
};
